import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Info } from "lucide-react";

export default function KreditPajakPage() {
  const nowY = new Date().getFullYear();
  const [tahun, setTahun] = useState(nowY);
  const [form, setForm] = useState({ tahun: nowY, pph21_dipotong: 0, pph2223_dipotong: 0, pph25_angsuran: 0, pph24_luar_negeri: 0, zakat: 0, keterangan: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => { const r = await api.get("/kredit-pajak", { params: { tahun } }); setForm((f) => ({ ...f, ...r.data, tahun })); };
  useEffect(() => { load(); }, [tahun]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { tahun: Number(tahun), pph21_dipotong: Number(form.pph21_dipotong) || 0, pph2223_dipotong: Number(form.pph2223_dipotong) || 0, pph25_angsuran: Number(form.pph25_angsuran) || 0, pph24_luar_negeri: Number(form.pph24_luar_negeri) || 0, zakat: Number(form.zakat) || 0, keterangan: form.keterangan || "" };
      await api.put("/kredit-pajak", payload);
      toast.success("Kredit pajak tersimpan");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const totalKredit = ["pph21_dipotong", "pph2223_dipotong", "pph25_angsuran", "pph24_luar_negeri"].reduce((s, k) => s + Number(form[k] || 0), 0);

  return (
    <>
      <PageHeader title="Kredit Pajak & Bukti Potong" subtitle="Pajak yang sudah dipotong pihak lain (mis. PPh 21 gaji dari PT) agar tidak bayar pajak dobel." actions={
        <Button onClick={save} disabled={saving} className="brand-btn rounded-sm" data-enter-submit data-testid="kredit-save"><Save size={14} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
      } />
      <div className="p-4 md:p-8 space-y-6 max-w-3xl">
        <div className="card-flat p-4 flex items-center gap-3">
          <Label>Tahun Pajak</Label>
          <Input type="number" className="w-32" value={tahun} onChange={(e) => setTahun(Number(e.target.value) || tahun)} data-enter-ignore data-testid="kredit-tahun" />
        </div>
        <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5"><Label>PPh 21 Dipotong (gaji dari PT / bukti potong 1721-A1)</Label><CurrencyInput value={form.pph21_dipotong} onValueChange={upd("pph21_dipotong")} /></div>
          <div className="space-y-1.5"><Label>PPh 22/23 Dipotong Pihak Lain</Label><CurrencyInput value={form.pph2223_dipotong} onValueChange={upd("pph2223_dipotong")} /></div>
          <div className="space-y-1.5"><Label>Angsuran PPh 25 (dibayar sendiri)</Label><CurrencyInput value={form.pph25_angsuran} onValueChange={upd("pph25_angsuran")} /></div>
          <div className="space-y-1.5"><Label>Kredit PPh 24 (Luar Negeri)</Label><CurrencyInput value={form.pph24_luar_negeri} onValueChange={upd("pph24_luar_negeri")} /></div>
          <div className="space-y-1.5"><Label>Zakat / Sumbangan Wajib (pengurang bruto)</Label><CurrencyInput value={form.zakat} onValueChange={upd("zakat")} /></div>
          <div className="space-y-1.5"><Label>Total Kredit Pajak</Label><div className="h-10 flex items-center font-mono text-lg font-semibold text-emerald-700">{rupiah(totalKredit)}</div></div>
          <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
        </div>
        <div className="card-flat p-4 flex gap-3 border-l-4 border-l-blue-500">
          <Info size={18} className="text-blue-600 shrink-0" />
          <div className="text-sm text-slate-600">Kredit pajak mengurangi <b>PPh terutang progresif</b>. Bila kredit &gt; terutang &rarr; muncul <b>PPh Lebih Bayar (28A)</b>; bila kurang &rarr; <b>PPh Kurang Bayar (29)</b> yang harus disetor. Lihat hasilnya di menu <b>Laporan Pajak (SPT 1770)</b>.</div>
        </div>
      </div>
    </>
  );
}
